import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvPaginatorComponent } from '../../../../Shared/Components/av-paginator/av-paginator.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { OnboardingsFacade } from '../../../../../Facades/Onboardings/onboardings.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  ONBOARDING_STATUS_LABEL,
  ONBOARDING_STATUS_OPTIONS,
  TENANT_HEALTH_LABEL,
  TENANT_HEALTH_TONE,
} from '../../../../../Consts/onboardings.const';
import { StatusTone } from '../../../../Shared/Types/status-tone.type';
import { OnboardingFunnelEntry } from '../../../../../Models/backoffice/onboarding-funnel.model';

@Component({
  selector: 'app-onboardings',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvPaginatorComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './onboardings.component.html',
  styleUrl: './onboardings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OnboardingsComponent {
  readonly facade = inject(OnboardingsFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly statusOptions = ONBOARDING_STATUS_OPTIONS;
  readonly statusLabelKeys = ONBOARDING_STATUS_LABEL;

  readonly isEmpty = computed<boolean>(
    () => this.facade.state() === 'ready' && this.facade.onboardings().length === 0,
  );

  healthLabelKey(status: string | null): string {
    return status === null ? this.unresolved : TENANT_HEALTH_LABEL[status] || status;
  }

  healthTone(status: string | null): StatusTone {
    return status === null ? 'neutral' : TENANT_HEALTH_TONE[status] || 'neutral';
  }

  consentUrl(entry: OnboardingFunnelEntry): string {
    return entry.tenant_id === null ? '' : this.facade.consentUrl(entry.tenant_id);
  }

  filterStatus(status: string): void {
    this.facade.filterStatus(status);
  }

  checkHealth(entry: OnboardingFunnelEntry): void {
    if (entry.organization === null) {
      return;
    }

    this.facade.checkTenantHealth(entry.organization.id);
  }

  openConsent(entry: OnboardingFunnelEntry): void {
    if (entry.tenant_id === null) {
      return;
    }

    this.facade.openConsent(entry.tenant_id);
  }

  goToPage(page: number): void {
    this.facade.goToPage(page);
  }

  changePageSize(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  reload(): void {
    this.facade.reload();
  }
}
